import { toMinor } from "../domain/money";
import type { Account, AppPreference, FxPoint, InstrumentEnrichment, Portfolio, Security, StoredQuote, TargetAllocation, Transaction, Watchlist, WatchlistItem } from "../domain/types";
import { db, type CherryDatabase } from "./database";

const timestamp = "2026-08-15T04:40:02.000Z";
const portfolioId = "fixture-portfolio";
const accountId = "fixture-account";
const symbols = ["NVDA", "TSM", "AAPL", "BABA", "UNH", "7203.T"];
const etfExposurePortfolioId = "fixture-etf-exposure-portfolio";

export function activeFixturePortfolioId(): string | null {
  if (!import.meta.env.DEV) return null;
  const fixture = new URLSearchParams(window.location.search).get("fixture");
  if (fixture === "standard") return portfolioId;
  if (fixture === "etf-exposure") return etfExposurePortfolioId;
  return null;
}

export function isStandardFixture(): boolean {
  return activeFixturePortfolioId() === portfolioId;
}

export function isRenderedFixture(): boolean {
  return activeFixturePortfolioId() != null;
}

export async function ensureRenderedFixture(database: CherryDatabase = db): Promise<void> {
  if (isStandardFixture()) await ensureStandardFixture(database);
  else if (activeFixturePortfolioId() === etfExposurePortfolioId) await ensureEtfExposureFixture(database);
}

export async function ensureStandardFixture(database: CherryDatabase = db): Promise<void> {
  if (!isStandardFixture()) return;
  const existing = await database.portfolios.get(portfolioId);
  const cashPreference: AppPreference = { key: `portfolio-cash:${portfolioId}`, value: { amountMinor: toMinor(12_440), currency: "USD", updatedAt: timestamp } };
  const fiftyTwoWeekHighs = [195.62, 212.6, 260.1, 148.43, 630.73, 3300];
  const fiftyTwoWeekLows = [86.62, 133.57, 169.21, 80.06, 412.31, 2400];
  const fixtureFx: FxPoint[] = [
    { id: "fixture-jpy-usd", baseCurrency: "JPY", quoteCurrency: "USD", date: timestamp.slice(0, 10), rate: "0.006851614035087719", source: "frankfurter", asOf: timestamp },
    { id: "fixture-usd-eur", baseCurrency: "USD", quoteCurrency: "EUR", date: timestamp.slice(0, 10), rate: "0.92", source: "frankfurter", asOf: timestamp },
    { id: "fixture-usd-pln", baseCurrency: "USD", quoteCurrency: "PLN", date: timestamp.slice(0, 10), rate: "3.65", source: "frankfurter", asOf: timestamp },
    { id: "fixture-usd-gbp", baseCurrency: "USD", quoteCurrency: "GBP", date: timestamp.slice(0, 10), rate: "0.79", source: "frankfurter", asOf: timestamp },
    { id: "fixture-usd-jpy", baseCurrency: "USD", quoteCurrency: "JPY", date: timestamp.slice(0, 10), rate: "146", source: "frankfurter", asOf: timestamp },
    { id: "fixture-usd-twd", baseCurrency: "USD", quoteCurrency: "TWD", date: timestamp.slice(0, 10), rate: "32.4", source: "frankfurter", asOf: timestamp },
    { id: "fixture-usd-hkd", baseCurrency: "USD", quoteCurrency: "HKD", date: timestamp.slice(0, 10), rate: "7.8", source: "frankfurter", asOf: timestamp },
  ];
  if (existing) {
    const existingQuotes = await Promise.all(symbols.map((symbol) => database.quotes.get(symbol)));
    const quoteUpdates = existingQuotes.flatMap((quote, index) => quote ? [{
      ...quote,
      fiftyTwoWeekHighMinor: toMinor(fiftyTwoWeekHighs[index], 2),
      fiftyTwoWeekLowMinor: toMinor(fiftyTwoWeekLows[index], 2),
    }] : []);
    await Promise.all([database.preferences.put(cashPreference), database.fxPoints.bulkPut(fixtureFx), database.quotes.bulkPut(quoteUpdates)]);
    return;
  }
  const portfolio: Portfolio = {
    id: portfolioId,
    name: "Core Portfolio",
    baseCurrency: "USD",
    benchmark: { kind: "single", components: [{ symbol: "^GSPC", weightBps: 10_000 }], rebalanceFrequency: "never" },
    costBasisMethod: "fifo",
    shortPositionPolicy: "enabled",
    createdAt: timestamp,
    updatedAt: timestamp
  };
  const account: Account = { id: accountId, portfolioId, name: "Brokerage", type: "brokerage", currency: "USD", includeInCash: true, createdAt: timestamp, updatedAt: timestamp };
  const securities: Security[] = symbols.map((symbol, index) => ({
    id: `fixture-security-${symbol}`,
    yahooSymbol: symbol,
    exchange: index === 5 ? "JPX" : index === 1 || index === 3 ? "NYSE" : "NASDAQ",
    currency: index === 5 ? "JPY" : "USD",
    type: index === 5 ? "equity" : "equity",
    metadata: {
      name: ["NVIDIA", "Taiwan Semiconductor", "Apple", "Alibaba", "UnitedHealth", "Toyota Motor"][index],
      sector: ["Technology", "Technology", "Technology", "Consumer", "Healthcare", "Consumer"][index],
      country: ["US", "TW", "US", "CN", "US", "JP"][index],
      category: ["Semiconductors", "Semiconductors", "Consumer technology", "E-commerce", "Managed care", "Automotive"][index],
    },
    overrides: {},
    createdAt: timestamp,
    updatedAt: timestamp
  }));
  const quantities = [500, 300, 200, 100, 50, 200];
  const costs = [150, 150, 180, 90, 500, 2459.45];
  const prices = [185.2, 198.6, 227.4, 84.4, 523.5, 2850];
  const transactions: Transaction[] = securities.map((security, index) => ({
    id: `fixture-transaction-${index}`,
    portfolioId,
    accountId,
    securityId: security.id,
    type: "opening_balance",
    tradeDate: "2025-01-02",
    settlementDate: "2025-01-02",
    quantity: String(quantities[index]),
    priceMinor: toMinor(costs[index], 2),
    amountMinor: toMinor(costs[index] * quantities[index], 2),
    currency: security.currency,
    scale: 2,
    feesMinor: "0",
    taxesMinor: "0",
    lotReferences: [],
    notes: "Rendered acceptance fixture",
    createdAt: timestamp,
    updatedAt: timestamp
  }));
  const quotes: StoredQuote[] = securities.map((security, index) => ({
    symbol: security.yahooSymbol,
    securityId: security.id,
    priceMinor: toMinor(prices[index], 2),
    fiftyTwoWeekHighMinor: toMinor(fiftyTwoWeekHighs[index], 2),
    fiftyTwoWeekLowMinor: toMinor(fiftyTwoWeekLows[index], 2),
    changeMinor: toMinor([3.41, 2.12, 1.36, -0.62, 4.31, 24][index], 2),
    changePercent: String([1.88, 1.08, 0.6, -0.73, 0.83, 0.85][index]),
    currency: security.currency,
    scale: 2,
    freshness: timestamp,
    source: "cache",
    status: index === 3 ? "stale" : "live",
    asOf: timestamp
  }));
  const targets: TargetAllocation[] = [
    { id: "fixture-target-tech", portfolioId, dimension: "sector", key: "Technology", targetBps: 3500, toleranceBps: 300 },
    { id: "fixture-target-health", portfolioId, dimension: "sector", key: "Healthcare", targetBps: 2000, toleranceBps: 250 },
    { id: "fixture-target-consumer", portfolioId, dimension: "sector", key: "Consumer", targetBps: 1500, toleranceBps: 200 },
    { id: "fixture-target-cash", portfolioId, dimension: "category", key: "Cash", targetBps: 800, toleranceBps: 200 }
  ];
  const watchlists: Watchlist[] = [
    { id: "fixture-watchlist", portfolioId, name: "Core ideas", createdAt: timestamp },
    { id: "fixture-watchlist-dividend", portfolioId, name: "Dividend watch", createdAt: timestamp },
    { id: "fixture-watchlist-asia", portfolioId, name: "Asia", createdAt: timestamp }
  ];
  const watchlistItems: WatchlistItem[] = [
    { id: "fixture-watch-aapl", watchlistId: watchlists[0].id, securityId: "fixture-security-AAPL", plannedWeightBps: 700, notes: "Services growth and product cycle", nextReviewDate: "2026-08-22" },
    { id: "fixture-watch-tsm", watchlistId: watchlists[0].id, securityId: "fixture-security-TSM", plannedWeightBps: 800, notes: "AI foundry demand and margin resilience", nextReviewDate: "2026-08-19" },
    { id: "fixture-watch-unh", watchlistId: watchlists[0].id, securityId: "fixture-security-UNH", plannedWeightBps: 500, notes: "Watch utilization trends and margin recovery", nextReviewDate: "2026-09-05" },
    { id: "fixture-watch-unh-dividend", watchlistId: watchlists[1].id, securityId: "fixture-security-UNH", plannedWeightBps: 400, notes: "Dividend growth and cash conversion", nextReviewDate: "2026-09-05" },
    { id: "fixture-watch-baba", watchlistId: watchlists[2].id, securityId: "fixture-security-BABA", notes: "Cloud growth and shareholder returns", nextReviewDate: "2026-08-29" },
    { id: "fixture-watch-toyota", watchlistId: watchlists[2].id, securityId: "fixture-security-7203.T", notes: "Hybrid demand and yen sensitivity", nextReviewDate: "2026-09-12" }
  ];
  await database.transaction("rw", [database.portfolios, database.accounts, database.securities, database.transactions, database.quotes, database.fxPoints, database.targetAllocations, database.watchlists, database.watchlistItems, database.preferences], async () => {
    await database.portfolios.put(portfolio);
    await database.accounts.put(account);
    await database.securities.bulkPut(securities);
    await database.transactions.bulkPut(transactions);
    await database.quotes.bulkPut(quotes);
    await database.fxPoints.bulkPut(fixtureFx);
    await database.targetAllocations.bulkPut(targets);
    await database.watchlists.bulkPut(watchlists);
    await database.watchlistItems.bulkPut(watchlistItems);
    await database.preferences.put(cashPreference);
  });
}

export async function ensureEtfExposureFixture(database: CherryDatabase = db): Promise<void> {
  if (activeFixturePortfolioId() !== etfExposurePortfolioId) return;
  const brokerageId = "fixture-etf-brokerage";
  const retirementId = "fixture-etf-retirement";
  const fixtureSymbols = ["MSFT", "VOO", "QQQ", "CSPX.L", "1306.T", "MYST"];
  const existingSecurities = await database.securities.where("yahooSymbol").anyOf(fixtureSymbols).toArray();
  const existingBySymbol = new Map(existingSecurities.map((security) => [security.yahooSymbol, security]));
  const securities: Security[] = fixtureSymbols.map((symbol, index) => ({
    id: existingBySymbol.get(symbol)?.id ?? `fixture-etf-security-${symbol}`,
    yahooSymbol: symbol,
    exchange: index === 3 ? "London" : index === 4 ? "Tokyo" : "NASDAQ",
    currency: index === 4 ? "JPY" : "USD",
    type: index === 0 ? "equity" : "etf",
    metadata: {
      name: ["Microsoft Corporation", "Vanguard S&P 500 ETF", "Invesco QQQ Trust", "iShares Core S&P 500 UCITS ETF", "NEXT FUNDS TOPIX ETF", "Mystery Global ETF"][index],
      fundFamily: index === 1 ? "Vanguard" : index === 2 ? "Invesco" : index === 3 ? "iShares" : index === 4 ? "NEXT FUNDS" : undefined,
      sector: index === 0 ? "Technology" : undefined,
      country: index === 3 ? "United Kingdom" : index === 4 ? "Japan" : "US",
      category: index === 0 ? "Software" : "Large blend",
    },
    overrides: {},
    createdAt: timestamp,
    updatedAt: timestamp,
  }));
  const portfolio: Portfolio = {
    id: etfExposurePortfolioId,
    name: "ETF Exposure Fixture",
    baseCurrency: "USD",
    benchmark: { kind: "single", components: [{ symbol: "^GSPC", weightBps: 10_000 }], rebalanceFrequency: "never" },
    costBasisMethod: "fifo",
    shortPositionPolicy: "enabled",
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  const accounts: Account[] = [
    { id: brokerageId, portfolioId: etfExposurePortfolioId, name: "Brokerage", type: "brokerage", currency: "USD", includeInCash: true, createdAt: timestamp, updatedAt: timestamp },
    { id: retirementId, portfolioId: etfExposurePortfolioId, name: "Retirement", type: "retirement", currency: "USD", includeInCash: true, createdAt: timestamp, updatedAt: timestamp },
  ];
  const transactions: Transaction[] = [
    ["msft", brokerageId, securities[0].id, "50", 300],
    ["voo-brokerage", brokerageId, securities[1].id, "60", 450],
    ["voo-retirement", retirementId, securities[1].id, "40", 450],
    ["qqq", retirementId, securities[2].id, "80", 400],
    ["cspx", brokerageId, securities[3].id, "30", 600],
    ["topix", retirementId, securities[4].id, "200", 3500],
    ["myst", brokerageId, securities[5].id, "100", 80],
  ].map(([id, fixtureAccountId, securityId, quantity, price]) => ({
    id: `fixture-etf-transaction-${id}`,
    portfolioId: etfExposurePortfolioId,
    accountId: String(fixtureAccountId),
    securityId: String(securityId),
    type: "opening_balance",
    tradeDate: "2025-01-02",
    settlementDate: "2025-01-02",
    quantity: String(quantity),
    priceMinor: toMinor(Number(price), 2),
    amountMinor: toMinor(Number(price) * Number(quantity), 2),
    currency: securities.find((security) => security.id === String(securityId))?.currency ?? "USD",
    scale: 2,
    feesMinor: "0",
    taxesMinor: "0",
    lotReferences: [],
    notes: "ETF exposure rendered acceptance fixture",
    createdAt: timestamp,
    updatedAt: timestamp,
  }));
  const quotes: StoredQuote[] = securities.map((security, index) => ({
    symbol: security.yahooSymbol,
    securityId: security.id,
    priceMinor: toMinor([400, 500, 500, 650, 3800, 100][index], 2),
    fiftyTwoWeekHighMinor: toMinor([468, 560, 540, 710, 4100, 110][index], 2),
    fiftyTwoWeekLowMinor: toMinor([340, 430, 400, 520, 3000, 70][index], 2),
    changeMinor: toMinor([2.4, 1.8, 2.1, 3.4, 18, -0.4][index], 2),
    changePercent: String([0.6, 0.36, 0.42, 0.52, 0.48, -0.4][index]),
    currency: security.currency,
    scale: 2,
    freshness: timestamp,
    source: "cache",
    status: "live",
    asOf: timestamp,
  }));
  const enrichments: InstrumentEnrichment[] = [
    {
      id: securities[1].id,
      symbol: "VOO",
      identifiers: { seriesId: "S000002839" },
      provenance: {},
      fundExposure: {
        source: "sec-nport",
        reportDate: "2026-03-31",
        filingDate: "2026-05-29",
        seriesId: "S000002839",
        sourceUrl: "https://www.sec.gov/Archives/edgar/data/36405/000175272426121234/xslFormNPORT-P_X01/primary_doc.xml",
        dimensions: {
          country: [{ label: "United States", weightPercent: "21" }],
          currency: [{ label: "USD", weightPercent: "21" }],
          category: [{ label: "Common equity", weightPercent: "21" }],
        },
        holdings: [
          { name: "Apple Inc", ticker: "AAPL", country: "United States", currency: "USD", category: "Common equity", weightPercent: "7" },
          { name: "Microsoft Corp", ticker: "MSFT", country: "United States", currency: "USD", category: "Common equity", weightPercent: "6" },
          { name: "NVIDIA Corp", ticker: "NVDA", country: "United States", currency: "USD", category: "Common equity", weightPercent: "5" },
          { name: "Amazon.com Inc", ticker: "AMZN", country: "United States", currency: "USD", category: "Common equity", weightPercent: "3" },
        ],
      },
      sources: [{ source: "sec-nport", status: "resolved" }],
      fetchedAt: timestamp,
      expiresAt: "2027-08-15T04:40:02.000Z",
    },
    {
      id: securities[2].id,
      symbol: "QQQ",
      identifiers: { seriesId: "S000001867" },
      provenance: {},
      fundExposure: {
        source: "sec-nport",
        reportDate: "2026-04-30",
        filingDate: "2026-06-29",
        seriesId: "S000001867",
        sourceUrl: "https://www.sec.gov/Archives/edgar/data/1067839/000175272426140321/xslFormNPORT-P_X01/primary_doc.xml",
        dimensions: {
          country: [{ label: "United States", weightPercent: "31" }],
          currency: [{ label: "USD", weightPercent: "31" }],
          category: [{ label: "Common equity", weightPercent: "31" }],
        },
        holdings: [
          { name: "Apple Inc.", ticker: "AAPL", country: "US", currency: "USD", category: "Common equity", weightPercent: "9" },
          { name: "Microsoft Corporation", ticker: "MSFT", country: "US", currency: "USD", category: "Common equity", weightPercent: "8" },
          { name: "NVIDIA Corporation", ticker: "NVDA", country: "US", currency: "USD", category: "Common equity", weightPercent: "7" },
          { name: "Alphabet Inc Class A", ticker: "GOOGL", country: "US", currency: "USD", category: "Common equity", weightPercent: "4" },
          { name: "Alphabet Inc Class C", ticker: "GOOG", country: "US", currency: "USD", category: "Common equity", weightPercent: "3" },
        ],
      },
      sources: [{ source: "sec-nport", status: "resolved" }],
      fetchedAt: timestamp,
      expiresAt: "2027-08-15T04:40:02.000Z",
    },
    {
      id: securities[3].id,
      symbol: "CSPX.L",
      identifiers: {},
      provenance: {},
      fundExposure: {
        source: "yahoo-finance",
        sourceUrl: "https://finance.yahoo.com/quote/CSPX.L/holdings/",
        dimensions: {},
        holdings: [
          { name: "Apple Inc.", ticker: "AAPL", country: "United States", weightPercent: "7.1" },
          { name: "Microsoft Corporation", ticker: "MSFT", country: "United States", weightPercent: "6.4" },
          { name: "NVIDIA Corporation", ticker: "NVDA", country: "United States", weightPercent: "5.8" },
          { name: "Amazon.com Inc", ticker: "AMZN", country: "United States", weightPercent: "3.5" },
        ],
      },
      sources: [{ source: "yahoo", status: "resolved" }],
      fetchedAt: timestamp,
      expiresAt: "2027-08-15T04:40:02.000Z",
    },
    {
      id: securities[4].id,
      symbol: "1306.T",
      identifiers: {},
      provenance: {},
      fundExposure: {
        source: "eodhd",
        reportDate: "2026-08-21",
        sourceUrl: "https://eodhd.com/financial-apis/stock-etfs-fundamental-data-feeds",
        dimensions: {},
        holdings: [
          { name: "Toyota Motor Corporation", ticker: "7203.T", country: "Japan", weightPercent: "3.7" },
          { name: "Mitsubishi UFJ Financial Group Inc", ticker: "8306.T", country: "Japan", weightPercent: "2.8" },
          { name: "Sony Group Corporation", ticker: "6758.T", country: "Japan", weightPercent: "2.6" },
          { name: "Hitachi Ltd", ticker: "6501.T", country: "Japan", weightPercent: "2.4" },
        ],
      },
      sources: [{ source: "eodhd", status: "resolved" }],
      fetchedAt: timestamp,
      expiresAt: "2027-08-15T04:40:02.000Z",
    },
  ];
  const fxPoints: FxPoint[] = [
    { id: "fixture-etf-jpy-usd", baseCurrency: "JPY", quoteCurrency: "USD", date: timestamp.slice(0, 10), rate: "0.006851614035087719", source: "frankfurter", asOf: timestamp },
  ];
  const cashPreference: AppPreference = { key: `portfolio-cash:${etfExposurePortfolioId}`, value: { amountMinor: toMinor(10_000), currency: "USD", updatedAt: timestamp } };
  await database.transaction("rw", [database.portfolios, database.accounts, database.securities, database.transactions, database.quotes, database.fxPoints, database.instrumentEnrichments, database.preferences], async () => {
    await database.portfolios.put(portfolio);
    await database.accounts.bulkPut(accounts);
    await database.securities.bulkPut(securities.filter((security) => !existingBySymbol.has(security.yahooSymbol)));
    await database.transactions.bulkPut(transactions);
    await database.quotes.bulkPut(quotes);
    await database.fxPoints.bulkPut(fxPoints);
    await database.instrumentEnrichments.bulkPut(enrichments);
    await database.preferences.put(cashPreference);
  });
}
